/**
 * The Overview's month-over-month trend, as pure functions.
 *
 * Every point is a ratio of sums over the person-month rows in scope — SUM(labor) /
 * SUM(billed claims), never an average of anyone's monthly rate (MEASURE-RULES §3).
 *
 * Two things can stand between a person's month row and the trend, and both are
 * WEIGHTS, not row filters, because the month grain is per person and neither one is:
 *
 *  - The claim filters (claim type, service type, claim status). The same per-person
 *    weight the filter bar applies to every tile — see `modeledWeight` in
 *    data/filters.tsx. Since review #156 (2026-10-06) `applyFilters` puts it on the
 *    scoped people's month rows and charge series itself, so callers pass only the
 *    slice's weight; passing the claim weight too would count it twice. (Before #79
 *    the trend ignored those filters; from #79 to #156 it applied them here.)
 *  - A slice of the Overview's bar chart — a claim department, state, payer type,
 *    facility group or facility. The bundle has no month grain by slice, only the
 *    whole-period crosstab, so each person's month rows are scaled by their share of
 *    the slice in it: claims by their claim share, dollars by their labor share. That
 *    assumes a person's mix is the same every month, which is what makes it modelled.
 *
 * The two multiply, as the claim filters already do with each other.
 */
import type { Person } from '@/data/selectors'
import type { CrosstabDict, TouchMonthRow } from '@/data/types'
import { touchBase, touchedBase } from './core'
import { windowCollection } from './collection'
import { sameValue } from '@/data/blankValue'
import { isNoClaim } from '@/data/noClaim'
import { isNoClaimMonth } from '@/data/hydrate'

type CrosstabRow = CrosstabDict['rows'][number]

/**
 * The crosstab rows inside the filter bar's Facility group filter, or null when it is
 * off. Groups are matched by name, every blank spelling as one "Unknown". The no-claim
 * bucket is payroll on no claim, filed under no facility, so no group selects it.
 */
export function groupRows(ct: CrosstabDict, groups: readonly string[]): ((row: CrosstabRow) => boolean) | null {
  if (groups.length === 0) return null
  const ok = ct.facilityGroups.map((g) => !isNoClaim(g) && groups.some((x) => sameValue(g, x)))
  return (row) => ok[row[4]] === true
}

/** Separate weights for counts and for dollars. See `modeledWeight`. */
export interface Weight {
  claims: number
  /** Labor, or — with `noClaim` given — labor in the months that billed claims. */
  labor: number
  /**
   * Labor in the months that billed NO claims: payroll on no claim (MEASURE-RULES §3.5),
   * which the crosstab files in its own no-claim cells. A slice of the crosstab weighs
   * it apart (`rowShares`), so a facility, state or payer takes none of it and the
   * no-claim bar takes nothing else. Absent, it is `labor`: a claim filter's weights do
   * not tell the two apart.
   */
  noClaim?: number
}

export const FULL: Weight = { claims: 1, labor: 1 }
const NONE: Weight = { claims: 0, labor: 0, noClaim: 0 }

/** The labor weight for one month row: its no-claim weight when it billed nothing. */
export const laborWeight = (w: Weight, m: Pick<TouchMonthRow, 'billedClaims' | 'noClaims'>) =>
  isNoClaimMonth(m) ? (w.noClaim ?? w.labor) : w.labor

/** A weight that leaves nothing of the person: outside the slice altogether. */
export const isNone = (w: Weight) => w.claims === 0 && w.labor === 0 && (w.noClaim ?? w.labor) === 0

/**
 * Each person's share of the crosstab rows `hit` picks, among the rows `within` keeps
 * (all of them when it is null): claims by claims, labor on claims by labor on claims,
 * and labor on no claim by labor on no claim. A person `hit` picks no row of is absent.
 *
 * Shares, not amounts, so a month range — under which each person's months are their
 * own and the crosstab is whole-period — cancels out: the share lands on the range's
 * months of the same kind. Keeping the two kinds of labor apart is what lets a facility
 * slice take none of the labor of months that billed nothing (a range used to hand it
 * the person's overall share of it).
 */
export function rowShares(
  ct: CrosstabDict,
  hit: (row: CrosstabRow) => boolean,
  within?: ((row: CrosstabRow) => boolean) | null,
): Map<string, Weight> {
  type Acc = { claims: number; labor: number; noClaim: number }
  const tot = new Map<number, Acc>()
  const got = new Map<number, Acc>()
  const add = (m: Map<number, Acc>, row: CrosstabRow, nc: boolean) => {
    let e = m.get(row[0])
    if (!e) m.set(row[0], (e = { claims: 0, labor: 0, noClaim: 0 }))
    e.claims += row[6]
    if (nc) e.noClaim += row[7]
    else e.labor += row[7]
  }
  const noClaimFac = ct.facilities.map((f) => isNoClaim(f))
  for (const row of ct.rows) {
    if (within && !within(row)) continue
    const nc = noClaimFac[row[5]] === true
    add(tot, row, nc)
    if (hit(row)) add(got, row, nc)
  }
  const share = (h: number, t: number) => (t === 0 ? 0 : h / t)
  const out = new Map<string, Weight>()
  for (const [e, h] of got) {
    const t = tot.get(e)!
    const name = ct.employees[e]
    if (!name) continue
    out.set(name, {
      claims: share(h.claims, t.claims),
      labor: share(h.labor, t.labor),
      noClaim: share(h.noClaim, t.noClaim),
    })
  }
  return out
}

/** The month-grain sums every trend metric is computed from. */
export interface MonthTotals {
  labor: number
  /** The claim count: weighted when the Claims switch is on Weighted. */
  billed: number
  /** Billed claims, never weighted: what closed % divides by. */
  billedStandard: number
  resolved: number
  /**
   * Rule-of-record no-touch claims and their base, the three touch buckets. The base
   * is NOT `resolved` — a different date basis. See measures/core.ts.
   */
  noTouch: number
  touchBase: number
  open: number
  openWorked: number
  facilities: number
  /** Touch count and the claims touched at least once, so this is a ratio of sums. */
  touches: number
  touched: number
  /**
   * Resolved charge and total charge over the MONTH'S collection window (bill months
   * M−8 … M−3), summed — the KPI's own SUM / SUM. See measures/collection.ts.
   */
  collNum: number
  collDen: number
  /** People contributing a row, for the per-employee averages. */
  people: number
}

/**
 * One month's sums over `people`, each row scaled by the person's weight. Null when
 * nobody in scope has a row that month — a gap in the line, not a zero.
 */
export function monthTotals(
  people: readonly Person[],
  month: string,
  weightOf: (p: Person) => Weight = () => FULL,
): MonthTotals | null {
  const t: MonthTotals = {
    labor: 0,
    billed: 0,
    billedStandard: 0,
    resolved: 0,
    noTouch: 0,
    touchBase: 0,
    open: 0,
    openWorked: 0,
    facilities: 0,
    touches: 0,
    touched: 0,
    collNum: 0,
    collDen: 0,
    people: 0,
  }
  let rows = 0
  for (const p of people) {
    const w = weightOf(p)
    // Outside the slice altogether: not a contributor, not even to "people".
    if (isNone(w)) continue
    const c = w.claims
    // Collection reads the month's lagged window off the person's series, not the
    // month row: the window reaches back to bills from months the person may have no
    // row in, and the KPI counts them, so this comes before the row test. Charge
    // travels with claims, so it takes the CLAIM weight — as `reweighted` scales a
    // person's own collection basis under a claim filter — not the labor weight,
    // which would scale it by where their hours went rather than what they billed.
    const coll = windowCollection(p.collectionMonths, month, month)
    if (coll !== null) {
      t.collNum += coll.resolved * c
      t.collDen += coll.total * c
    }
    const row: TouchMonthRow | undefined = p.months.find((m) => m.month === month)
    if (!row) continue
    t.labor += row.claimLabor * laborWeight(w, row)
    t.billed += (row.weightedClaims ?? row.billedClaims) * c
    t.billedStandard += row.billedClaims * c
    // Everything below is not split by claim type or slice in the bundle; it takes the
    // claim weight, as `reweighted` does for the person's own totals.
    t.resolved += row.resolvedClaims * c
    t.noTouch += row.resolvedNoTouch * c
    t.touchBase += touchBase(row) * c
    t.open += row.openClaims * c
    t.openWorked += row.openClaimsWorked * c
    t.touches += row.resolvedTouches * c
    t.touched += touchedBase(row) * c
    // A distinct count cannot be scaled; it is the facilities of the people who work
    // the scope. Over the people who carry claims, so zero-claim staff — who hold no
    // facility — do not dilute the average.
    t.facilities += row.facilities
    if (p.claims > 0) t.people += 1
    rows += 1
  }
  return rows === 0 ? null : t
}

/** One line of the trend: a label and a point per month. */
export interface TrendSeries {
  key: string
  label: string
  points: (number | null)[]
}

/**
 * One line per department, each point `of(month totals)`. A department with nobody
 * in scope carrying claims or labor is left out rather than drawn flat at nothing.
 *
 * Everyone in the department counts, claims or not: cost per claim's numerator
 * includes unattributed labor (MEASURE-RULES §3.2), and a person paid with no claims is
 * the purest case of it.
 */
export function departmentTrend(
  departments: readonly string[],
  people: readonly Person[],
  months: readonly string[],
  of: (t: MonthTotals) => number | null,
  weightOf: (p: Person) => Weight = () => FULL,
): TrendSeries[] {
  const out: TrendSeries[] = []
  for (const d of departments) {
    const inDept = people.filter((p) => p.department === d)
    if (!inDept.some((p) => p.claims > 0 && weightOf(p).claims > 0)) continue
    const points = months.map((m) => {
      const t = monthTotals(inDept, m, weightOf)
      return t === null ? null : of(t)
    })
    out.push({ key: d, label: d, points })
  }
  return out
}

export const ALL_IN_SCOPE = '__all__'

/**
 * Everyone in scope pooled into one line: the figure the masthead tiles quote, and the
 * bar a pick came from, month by month. The department lines cannot show it — a
 * department whose labor carries no claims (Part B's, unattributed) has no line, but
 * its labor is in every pooled cost per claim (MEASURE-RULES §3.2).
 */
export function pooledTrend(
  people: readonly Person[],
  months: readonly string[],
  of: (t: MonthTotals) => number | null,
  weightOf: (p: Person) => Weight = () => FULL,
): TrendSeries {
  return {
    key: ALL_IN_SCOPE,
    label: 'All in scope',
    points: months.map((m) => {
      const t = monthTotals(people, m, weightOf)
      return t === null ? null : of(t)
    }),
  }
}

/** What a slice of the crosstab is narrowed to. An absent key is "all". */
export interface SliceMatch {
  department?: string | null
  state?: string | null
  payer?: string | null
  facilityGroup?: string | null
  facility?: string | null
}

export const isSliced = (m: SliceMatch) =>
  Boolean(m.department || m.state || m.payer || m.facilityGroup || m.facility)

/**
 * Each person's share of a slice, from the whole-period crosstab: their claims in the
 * slice over all their crosstab claims, and the same for labor. Shares, not amounts,
 * so a month range — which scales a person's crosstab rows uniformly — cancels out.
 *
 * Null when nothing is sliced. Anyone the crosstab does not have, or who has none of
 * the slice, gets a weight of 0 — they do not work it, and the bars leave them out too.
 *
 * `within` (the Facility group filter, `groupRows`) limits both sides: a person's share
 * of the slice among their rows in those groups. The people it is applied to have
 * already been scaled to the groups (data/filters.tsx), so the two multiply to their
 * share of the slice, not that share times the groups' again.
 */
export function sliceWeights(
  ct: CrosstabDict,
  match: SliceMatch,
  within?: ((row: CrosstabRow) => boolean) | null,
): ((p: Person) => Weight) | null {
  if (!isSliced(match)) return null
  // Blank spellings are one "Unknown", as the bars draw them (review #177).
  const inSlice = (row: CrosstabRow) => {
    const [, dept, state, payer, group, fac] = row
    if (match.department && !sameValue(ct.departments[dept], match.department)) return false
    if (match.state && !sameValue(ct.states[state], match.state)) return false
    if (match.payer && !sameValue(ct.payers[payer], match.payer)) return false
    if (match.facilityGroup && !sameValue(ct.facilityGroups[group], match.facilityGroup)) return false
    if (match.facility && !sameValue(ct.facilities[fac], match.facility)) return false
    return true
  }
  const byName = rowShares(ct, inSlice, within)
  return (p) => byName.get(p.employee) ?? NONE
}

/** Two weights applied together: a claim filter and a slice. */
export const times =
  (a: (p: Person) => Weight, b: (p: Person) => Weight) =>
  (p: Person): Weight => {
    const x = a(p)
    const y = b(p)
    return {
      claims: x.claims * y.claims,
      labor: x.labor * y.labor,
      noClaim: (x.noClaim ?? x.labor) * (y.noClaim ?? y.labor),
    }
  }

/** One row of the trend's table view: a line's months, and its total where it has one. */
export interface TrendTableRow {
  key: string
  label: string
  cells: (number | null)[]
  /** The months summed, for a count or dollars. Null for a rate: see `trendTableRows`. */
  total: number | null
  /** The everyone-in-scope row, kept under the departments. */
  pooled: boolean
}

/**
 * The trend as a table (review #858): a row per line, then "All in scope".
 *
 * The chart leaves the pooled line off a count — drawn beside its parts it flattens
 * them against the axis — but a table has no axis, so the row is always there and the
 * departments' column adds up to it. Only a count or dollars gets a total: the months
 * summed. A rate's months cannot be summed or averaged into the range's rate (SUM /
 * SUM, MEASURE-RULES §3); the range's own figure is the masthead's.
 */
export function trendTableRows(
  lines: readonly TrendSeries[],
  pooled: TrendSeries | null,
  additive: boolean,
): TrendTableRow[] {
  const row = (s: TrendSeries, isPooled: boolean): TrendTableRow => {
    const seen = s.points.filter((v): v is number => v !== null)
    return {
      key: s.key,
      label: s.label,
      cells: s.points,
      total: additive && seen.length > 0 ? seen.reduce((a, v) => a + v, 0) : null,
      pooled: isPooled,
    }
  }
  const out = lines.filter((l) => l.key !== ALL_IN_SCOPE).map((l) => row(l, false))
  if (pooled) out.push(row(pooled, true))
  return out
}
